import {useState, useEffect, useMemo} from 'react';
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription} from '@/components/ui/dialog';
import {Button} from '@/components/ui/button';
import {ChevronLeft, ChevronRight, Check, Eye, Globe} from 'lucide-react';
import {Reward} from '@/types/rewards';
import {RewardFormData, Language, Step} from '../types';
import {StepIndicator} from './StepIndicator';
import {FormStep1Content} from './FormStep1Content';
import {FormStep2Content} from './FormStep2Content';
import {FormStep3Content} from './FormStep3Content';
import {QuizPageBuilder} from './QuizPageBuilder';
import {RewardPreview} from './RewardPreview';

interface RewardFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: RewardFormData) => void;
    isEditing: boolean;
    isPending: boolean;
    initialData?: Reward;
    domains: any[];
    availableLanguages: Language[];
}

function getSteps(type: 'standard' | 'quiz'): Step[] {
    if (type === 'quiz') {
        return [
            {number: 1, title: 'Conteúdo', description: 'Título, descrição e textos'},
            {number: 2, title: 'Quiz', description: 'Páginas e perguntas'},
            {number: 3, title: 'Cores', description: 'Personalização visual'},
            {number: 4, title: 'Configurações', description: 'Timing e idiomas'},
        ];
    }
    return [
        {number: 1, title: 'Conteúdo', description: 'Título, descrição e textos'},
        {number: 2, title: 'Cores', description: 'Personalização visual'},
        {number: 3, title: 'Configurações', description: 'Domínio, timing e idiomas'},
    ];
}

const INITIAL_FORM_DATA: RewardFormData = {
    type: 'standard',
    title: '',
    description: '',
    trigger_time: 0,
    frequency_time: 10,
    link: '',
    button_text: '',
    button_color: '#3B82F6',
    button_text_color: '#FFFFFF',
    background_color: '#FFFFFF',
    text_color: '#52fa8E',
    closed: true,
    show_branding: true,
    languages: [],
};

export const RewardFormModal = ({
                                    isOpen,
                                    onClose,
                                    onSubmit,
                                    isEditing,
                                    isPending,
                                    initialData,
                                    domains,
                                    availableLanguages
                                }: RewardFormModalProps) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [showTranslationsPreview, setShowTranslationsPreview] = useState(false);
    const [languageSearch, setLanguageSearch] = useState('');

    const [formData, setFormData] = useState<RewardFormData>(INITIAL_FORM_DATA);

    const steps = useMemo(() => getSteps(formData.type || 'standard'), [formData.type]);
    const totalSteps = steps.length;

    // Sincronizar formData com initialData quando o modal abrir para edição
    useEffect(() => {
        if (isOpen && initialData) {
            setFormData({
                type: initialData.type || 'standard',
                title: initialData.title,
                description: initialData.description,
                trigger_time: initialData.trigger_time,
                frequency_time: initialData.frequency_time,
                link: initialData.link,
                button_text: initialData.button_text,
                button_color: initialData.button_color || '#3B82F6',
                button_text_color: initialData.button_text_color || '#FFFFFF',
                background_color: initialData.background_color || '#FFFFFF',
                text_color: initialData.text_color || '#52fa8E',
                closed: initialData.closed || false,
                // `!== false` e nao `|| true`: offerwall gravado antes da coluna
                // existir vem sem o campo, e nesse caso o bloco fica ligado.
                show_branding: initialData.show_branding !== false,
                languages: (initialData.languages || []) as string[],
                quiz_config: initialData.quiz_config || undefined,
            });
        } else if (isOpen && !initialData) {
            setFormData(INITIAL_FORM_DATA);
        }
    }, [isOpen, initialData]);

    // Resetar quando o modal fechar
    useEffect(() => {
        if (!isOpen) {
            setCurrentStep(1);
            setShowTranslationsPreview(false);
            setLanguageSearch('');
        }
    }, [isOpen]);

    // When type changes, reset step if current step is beyond new total
    useEffect(() => {
        if (currentStep > totalSteps) {
            setCurrentStep(totalSteps);
        }
    }, [totalSteps, currentStep]);

    const handleClose = () => {
        onClose();
    };

    const handleFormChange = (data: Partial<RewardFormData>) => {
        setFormData(prev => ({...prev, ...data}));
    };

    const addLanguage = (code: string) => {
        if (!formData.languages.includes(code)) {
            setFormData(prev => ({
                ...prev,
                languages: [...prev.languages, code]
            }));
        }
    };

    const removeLanguage = (code: string) => {
        setFormData(prev => ({
            ...prev,
            languages: prev.languages.filter(l => l !== code)
        }));
    };

    const nextStep = () => {
        if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
    };

    const prevStep = () => {
        if (currentStep > 1) setCurrentStep(currentStep - 1);
    };

    const handleSubmit = () => {
        onSubmit(formData);
    };

    const isStep1Valid = formData.type === 'quiz'
        ? formData.title && formData.button_text
        : formData.title && formData.description && formData.button_text;
    const isQuizStepValid = formData.type !== 'quiz' || (formData.quiz_config && formData.quiz_config.pages.length > 0);
    const isLastStepValid = formData.languages.length > 0;
    const isFormValid = isStep1Valid && isQuizStepValid && isLastStepValid;

    // Determine which content to show based on step and type
    const isQuiz = formData.type === 'quiz';
    const colorsStep = isQuiz ? 3 : 2;
    const configStep = isQuiz ? 4 : 3;

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="max-w-7xl max-h-[90vh] overflow-hidden">
                <DialogHeader>
                    <DialogTitle>
                        {isEditing ? 'Editar Offerwall' : 'Criar Novo Offerwall'}
                    </DialogTitle>
                    <DialogDescription>
                        Preencha os campos em {totalSteps} etapas simples
                    </DialogDescription>
                </DialogHeader>

                <div className="grid md:grid-cols-[1fr,400px] gap-8 mt-4">
                    <div className="overflow-y-auto max-h-[calc(90vh-200px)] pr-4">
                        <StepIndicator steps={steps} currentStep={currentStep}/>

                        {currentStep === 1 && (
                            <FormStep1Content
                                formData={formData}
                                onChange={handleFormChange}
                                disabled={isPending}
                            />
                        )}

                        {isQuiz && currentStep === 2 && (
                            <QuizPageBuilder
                                quizConfig={formData.quiz_config || {pages: []}}
                                onChange={(config) => handleFormChange({quiz_config: config})}
                                disabled={isPending}
                            />
                        )}

                        {currentStep === colorsStep && (
                            <FormStep2Content
                                formData={formData}
                                onChange={handleFormChange}
                                disabled={isPending}
                            />
                        )}

                        {currentStep === configStep && (
                            <FormStep3Content
                                formData={formData}
                                onChange={handleFormChange}
                                disabled={isPending}
                                domains={domains}
                                availableLanguages={availableLanguages}
                                languageSearch={languageSearch}
                                onLanguageSearchChange={setLanguageSearch}
                                onAddLanguage={addLanguage}
                                onRemoveLanguage={removeLanguage}
                            />
                        )}

                        <div className="flex gap-3 pt-6 mt-6 border-t">
                            {currentStep > 1 && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={prevStep}
                                    disabled={isPending}
                                >
                                    <ChevronLeft className="mr-2 h-4 w-4"/>
                                    Anterior
                                </Button>
                            )}

                            {currentStep < totalSteps ? (
                                <Button
                                    type="button"
                                    onClick={nextStep}
                                    disabled={
                                        (currentStep === 1 && !isStep1Valid) ||
                                        (isQuiz && currentStep === 2 && !isQuizStepValid) ||
                                        isPending
                                    }
                                    className="ml-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                                >
                                    Próximo
                                    <ChevronRight className="ml-2 h-4 w-4"/>
                                </Button>
                            ) : (
                                <div className="ml-auto flex gap-3">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleClose}
                                        disabled={isPending}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        onClick={handleSubmit}
                                        disabled={!isFormValid || isPending}
                                        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                                    >
                                        {isPending ? (
                                            'Salvando...'
                                        ) : (
                                            <>
                                                <Check className="mr-2 h-4 w-4"/>
                                                {isEditing ? 'Atualizar' : 'Criar'}
                                            </>
                                        )}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="space-y-4 border-l pl-8">
                        <div className="sticky top-0">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <Eye className="h-5 w-5 text-gray-600"/>
                                    <h3 className="font-semibold text-gray-900">Preview</h3>
                                </div>

                                {isEditing && initialData?.translations && initialData.translations.length > 0 && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setShowTranslationsPreview(!showTranslationsPreview)}
                                    >
                                        <Globe className="h-4 w-4 mr-2"/>
                                        {showTranslationsPreview ? 'Original' : 'Traduções'}
                                    </Button>
                                )}
                            </div>

                            <div className="bg-gray-50 rounded-lg p-6">
                                {showTranslationsPreview && initialData?.translations ? (
                                    <div className="space-y-6 max-h-[calc(90vh-300px)] overflow-y-auto">
                                        {initialData.translations.map((translation) => (
                                            <RewardPreview
                                                key={translation.id}
                                                title={translation.title}
                                                description={translation.description}
                                                buttonText={translation.button_text}
                                                buttonColor={formData.button_color}
                                                buttonTextColor={formData.button_text_color}
                                                backgroundColor={formData.background_color}
                                                textColor={formData.text_color}
                                                showCloseButton={formData.closed}
                                                showBranding={formData.show_branding}
                                                language={availableLanguages.find(l => l.code === translation.language)?.language}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <RewardPreview
                                        title={formData.title}
                                        description={formData.description}
                                        buttonText={formData.button_text}
                                        buttonColor={formData.button_color}
                                        buttonTextColor={formData.button_text_color}
                                        backgroundColor={formData.background_color}
                                        textColor={formData.text_color}
                                        showCloseButton={formData.closed}
                                        rewardType={formData.type}
                                        quizConfig={formData.quiz_config}
                                    />
                                )}
                            </div>

                            <p className="text-xs text-gray-500 text-center mt-2">
                                {showTranslationsPreview
                                    ? 'Visualizando traduções geradas'
                                    : 'As alterações aparecem instantaneamente'
                                }
                            </p>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
